<template>
    <div class="full-height bg-white">
        <!-- Report panel header starts here-->
        <div
            class="row justify-between items-center bg-primary q-px-md q-py-sm"
        >
            <q-btn-group>
                <q-btn
                    @click="isTableMode = true"
                    :color="isTableMode ? 'primary' : 'white'"
                    :text-color="isTableMode ? 'white' : 'primary'"
                    class="toggle-report-view-button"
                    icon="fas fa-table"
                    size="10px"
                    :outline="!isTableMode"
                >
                    <q-tooltip>
                        {{ $t('Switch to table view') }}
                    </q-tooltip>
                </q-btn>
                <q-btn
                    @click="isTableMode = false"
                    :color="isTableMode ? 'white' : 'primary'"
                    :text-color="isTableMode ? 'primary' : 'white'"
                    class="toggle-report-view-button"
                    icon="fas fa-chart-bar"
                    size="10px"
                    :outline="isTableMode"
                >
                    <q-tooltip>
                        {{ $t('Switch to chart view') }}
                    </q-tooltip>
                </q-btn>
            </q-btn-group>

            <div class="float-right">
                <q-btn-group flat v-if="!$q.platform.is.mobile">
                    <q-btn
                        id="download-csv-button"
                        @click="exportDataAsCSV"
                        :loading="isDownloadingCSV"
                        class="download-file-buttons"
                        icon="far fa-file-csv"
                        text-color="white"
                        size="12px"
                        flat
                    >
                        <q-tooltip style="font-size: 14px">
                            {{ $t('Export raw data (CSV)') }}
                            <br />
                            {{
                                $t(
                                    'Filtered elements will not be present on the downloadable report.'
                                )
                            }}
                        </q-tooltip>
                    </q-btn>
                    <q-btn
                        id="download-xls-button"
                        @click="exportDataAsXLS"
                        :loading="isDownloadingXLS"
                        text-color="white"
                        class="download-file-buttons"
                        icon="far fa-file-excel"
                        size="12px"
                        flat
                    >
                        <q-tooltip style="font-size: 14px">
                            {{ $t('Export raw data (XLS)') }}
                            <br />
                            {{
                                $t(
                                    'Filtered elements will not be present on the downloadable report.'
                                )
                            }}
                        </q-tooltip>
                    </q-btn>
                </q-btn-group>
            </div>
        </div>
        <!-- Report panel header ends here -->

        <!-- Report table -->
        <loss-report-table
            v-if="isTableMode"
            :report="report"
            :selectedSecondaryGrouping="selectedSecondaryGrouping"
            :selectedGroupBy="selectedGroupBy"
            @setOrder="setOrder"
            @handleShowEvents="handleShowEvents"
        />

        <!-- Report Charts -->
        <div v-if="!isTableMode" class="report-container">
            <loss-dashboard
                :report="report"
                :facility="facilityId"
                :selectedGroupBy="selectedGroupBy"
            ></loss-dashboard>
        </div>

        <!-- Events table -->
        <q-dialog v-model="showEvents">
            <events-table
                @close="showEvents = false"
                :eventsData="eventsData"
            ></events-table>
        </q-dialog>
    </div>
</template>

<script>
import EventsTable from '@components/EventsTable.vue';
import { getLocaleFormat } from '@models/timeformats.js';
import FileExporter from '@utils/FileExporter.js';
import { logFileDownloadMessage } from '@utils/logMessagesForE2ETesting.js';
import LossDashboard from './LossDashboard.vue';
import {
    buildLossReport,
    buildTable,
    getCauseName,
    transformSpeedLossRecords
} from './lossReport.js';
import LossReportTable from './LossReportTable.vue';
import { mapState } from 'pinia';
import { useAssetsStore } from '@stores/assets.ts';
import { useFacilitiesStore } from '@stores/facilities.ts';
import { useReportsStore } from '@stores/reports.js';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'LossReport',
    components: { EventsTable, LossDashboard, LossReportTable },
    props: {
        records: {
            required: true,
            validator(val) {
                return Array.isArray(val) || val === null;
            }
        },
        speedLossRecords: {
            required: false,
            default: null,
            validator(val) {
                return (
                    val === null ||
                    (typeof val === 'object' &&
                        (Array.isArray(val.currentReportData) ||
                            val.currentReportData === null))
                );
            }
        },
        facilityId: {
            required: true
        },
        selectedAssets: {
            type: Set,
            required: true
        },
        selectedDowntimes: {
            type: Set,
            required: true
        },
        selectedLosses: {
            type: Set,
            required: true
        },
        selectedShifts: {
            type: Set,
            required: true
        },
        shiftIdToNames: {
            type: Object,
            required: true
        },
        selectedGroupBy: {
            type: String,
            required: false,
            default: 'causes'
        },
        selectedSecondaryGrouping: {
            type: String,
            required: false,
            default: 'assets'
        }
    },
    created() {
        // initialize file exporter instance
        this.fileExporter = new FileExporter({
            headers: this.fileHeaders
        });
    },
    data() {
        return {
            // default cause id for unjustified events
            OTHER_MICRO_CAUSE_ID: -1,
            OTHER_MAJOR_AVAILABILITY_CAUSE_ID: -2,
            OTHER_MAJOR_PERFORMANCE_CAUSE_ID: -3,
            orderBy: 'totalDuration',
            descending: true,

            // events dialog for an asset
            showEvents: false,
            eventsData: {
                assetId: null,
                productId: null,
                productName: null,
                planned: null,
                typeOfLoss: null,
                start: null,
                end: null,
                isMinor: null
            },

            // button loading animation
            isDownloadingCSV: false,
            isDownloadingXLS: false,

            // report/table view toggler
            isTableMode: true,

            // file exporter class
            fileExporter: null
        };
    },
    computed: {
        ...mapState(useAssetsStore, ['getAsset']),
        ...mapState(useFacilitiesStore, ['getFacility']),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get the file headers for file exporting
         * @return {Array} file headers for exporting
         */
        fileHeaders() {
            return [
                this.$t('Cause'),
                this.$t('Asset'),
                this.$t('Product'),
                this.$t('SKU'),
                this.$t('Shift'),
                this.$t('Downtime duration (sec)'),
                this.$t('Micro'),
                this.$t('Loss type'),
                this.$t('Planned'),
                this.$t('Event Start Date').concat(` [${this.localTimezone}]`),
                this.$t('Event End Date').concat(` [${this.localTimezone}]`),
                this.$t('Report Start Date').concat(` [${this.localTimezone}]`),
                this.$t('Report End Date').concat(` [${this.localTimezone}]`)
            ];
        },
        /**
         * Get the file rows for file exporting
         * @return {Array} file rows for exporting
         */
        fileRows() {
            let rows = [];

            const filteredRecords = this.combinedRecords.filter(record => {
                return (
                    this.filters.assetIds.has(record['asset_id']) &&
                    this.filters.shiftIds.has(record['shift_id']) &&
                    ((this.filters.minor && record['minor']) ||
                        (this.filters.major && !record['minor'])) &&
                    ((this.filters.planned && record['planned']) ||
                        (this.filters.unplanned && !record['planned'])) &&
                    ((this.filters.availability &&
                        record['type_of_loss'] === 'availability') ||
                        (this.filters.performance &&
                            record['type_of_loss'] === 'performance'))
                );
            });

            for (const record of filteredRecords) {
                // rename null causes
                let causeName = getCauseName(record);
                // only include assets that are found in the report
                const asset = this.getAsset(record['asset_id']);
                if (asset) {
                    let row = [
                        // enclose names in quotes in case they contain commas
                        causeName,
                        // append (Deleted) to asset name if asset is deleted
                        !asset.is_deleted
                            ? asset.name
                            : this.$t('{assetName} (Deleted)', {
                                  assetName: asset.name
                              }),
                        record['product_name'],
                        record['sku'],
                        this.shiftIdToNames[record['shift_id']],
                        parseInt(record['downtime_duration']),
                        record['minor'],
                        record['type_of_loss'],
                        record['planned'],
                        record['event_start_date']
                            ? this.formatDate(record['event_start_date'])
                            : '',
                        record['event_end_date']
                            ? this.formatDate(record['event_end_date'])
                            : '',
                        this.formatDate(this.getDateRange.start),
                        this.formatDate(this.getDateRange.end)
                    ];
                    rows.push(row);
                }
            }

            return rows;
        },
        /**
         * Combine downtime records with transformed speed loss records
         * @return {Array} Combined records for loss report processing
         */
        combinedRecords() {
            // Start with downtime records (or empty array if null)
            const downtimeRecords = this.records || [];

            // Transform and add speed loss records if available
            if (
                this.speedLossRecords &&
                Array.isArray(this.speedLossRecords.currentReportData)
            ) {
                const transformedSpeedLosses = transformSpeedLossRecords(
                    this.speedLossRecords.currentReportData,
                    this.getDateRange.start,
                    this.getDateRange.end
                );
                return [...downtimeRecords, ...transformedSpeedLosses];
            }

            return downtimeRecords;
        },
        /**
         * Build report data of current interval
         * @return {Object} report data containing table data and date interval
         */
        report() {
            // Build report data directly without intermediate variable
            const tableData = buildTable(
                buildLossReport(
                    this.combinedRecords,
                    this.filters,
                    this.selectedSecondaryGrouping
                ),
                this.selectedGroupBy,
                this.selectedSecondaryGrouping,
                this.orderBy,
                this.descending
            );

            // Use Object.assign for better memory efficiency than spread operator
            // This modifies tableData directly instead of creating a new object
            tableData.dateInterval = {
                start: this.getDateRange.start,
                end: this.getDateRange.end
            };

            return tableData;
        },
        /**
         * All possible filters for this report
         * @return {Object} all possible filters for this report
         */
        filters() {
            return {
                minor: this.selectedDowntimes.has('minor'),
                major: this.selectedDowntimes.has('major'),
                planned: this.selectedDowntimes.has('planned'),
                unplanned: this.selectedDowntimes.has('unplanned'),
                availability: this.selectedLosses.has('availability'),
                performance: this.selectedLosses.has('performance'),
                assetIds: this.selectedAssets,
                shiftIds: this.selectedShifts
            };
        },
        /**
         * Extract facility name from facility id
         * @return {String} facility name
         */
        facilityName() {
            return this.getFacility(this.facilityId).name.toLowerCase();
        },
        /**
         * Get local timezone
         * @return {String} local timezone
         */
        localTimezone() {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) {
                return timezone;
            }
            return 'UTC';
        }
    },
    methods: {
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Handles ordering of report table
         * Note: Use Object for named parameters to avoid linting errors
         * @param {String} orderBy - column name to order by
         * @param {Boolean} descending - order direction
         */
        setOrder({ orderBy, descending }) {
            this.orderBy = orderBy;
            this.descending = descending;
        },
        /**
         * Handle show events dialog.
         * Note: Use Object for named parameters to avoid linting errors
         * @param {Object} row row data of the clicked row
         * @param {Object} dateInterval date interval of the clicked row
         */
        handleShowEvents({ row, dateInterval }) {
            // set URL query values to be used by event dialog
            this.eventsData = {
                groupBy: this.selectedGroupBy,
                assetId: row.assetId,
                productId: row.productId,
                productName: row.productName,
                name: row.name,
                id: row.id,
                typeOfLoss: row.typeOfLoss,
                planned:
                    this.filters.planned && this.filters.unplanned
                        ? null
                        : !this.filters.unplanned,
                start: dateInterval.start,
                end: dateInterval.end,
                isMinor:
                    this.filters.major && this.filters.minor
                        ? null
                        : this.filters.minor
            };
            if (row.id <= 0) {
                // the records are categorized by cause id. Events that don't have a cause
                // can be either "Other (major)" or "Other (minor)" events
                this.eventsData.id = null;
                // for records which do not have cause or category the minor is determined
                // according to the row id which could be -1 (for minor) and -2 (for major)
                this.eventsData.isMinor = row.id === this.OTHER_MICRO_CAUSE_ID;
                // this.eventsData.planned = row.planned;
            }
            // show events dialog
            this.showEvents = true;
        },
        /**
         * Export raw records as CSV.
         */
        async exportDataAsCSV() {
            // begin loading
            this.isDownloadingCSV = true;

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            try {
                await this.fileExporter.exportCSV({
                    filename: `${this.facilityName}_losses_${start_date}_${end_date}`,
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('CSV');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }
            // end loading
            this.isDownloadingCSV = false;
        },
        /**
         * Export raw records as XLS.
         */
        exportDataAsXLS() {
            // begin loading
            this.isDownloadingXLS = true;
            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            try {
                this.fileExporter.exportXLS({
                    filename: `${this.facilityName}_losses_${start_date}_${end_date}`,
                    sheetName: this.$t('Losses Report'),
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('XLS');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    ),
                    error
                );
            }
            // end loading
            this.isDownloadingXLS = false;
        }
    }
};
</script>

<style lang="scss" scoped>
$table-height: 100%;
$report-header-panel-height: 50px;

.toggle-report-view-button :deep(.q-btn__wrapper) {
    padding: 6px;
}

:deep(.report-container) {
    border: 1px solid grey;
    height: calc(#{$table-height} - #{$report-header-panel-height});
}

.report-panel-header {
    height: $report-header-panel-height;
}
</style>
